import Scene from "./Scene";
import common from "../common";
import {translate} from "../utils";
import Drawing from "../drawing/Drawing";
import DrawingTextureHack from "../drawing/DrawingTextureHack";
import DrawingSpine from "../drawing/DrawingSpine";
import ConfirmDialog from "../ui/ConfirmDialog";
import BlueprintCarousel from "../ui/BlueprintCarousel";
import ProgressBar from "../ui/ProgressBar";
import ColorPanel from "../ui/ColorPanel";
import ConfirmRestartDialog from "../ui/ConfirmRestartDialog";
import {Emitter} from "pixi-particles";
import {GlowFilter} from "pixi-filters";

export default class DrawingScene extends Scene {

    constructor() {
        super();

        this._slotIndex = -1;

        this._paths = [];

        this._drawLock = true;

        this._groupcount = 0;

    }

    init() {
        super.init();

        this._alphaFilter = new PIXI.filters.AlphaFilter(0.6);
        this._glowFilter = new GlowFilter({
            distance: 30,
            outerStrength: 1,
            innerStrength: 1,
            color: 0x7efd8f,
        });

        this._drawingLayer = new PIXI.Container();
        this.addChild(this._drawingLayer);

        this._bg = new PIXI.Sprite(P3.assets.texture("bg_drawing"));
        this._bg.anchor.set(0.5);
        this._bg.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this._bg.width = common.STAGE_WIDTH * 2;
        this._bg.height = common.STAGE_HEIGHT * 2;
        this._drawingLayer.addChild(this._bg);

        this._paper = new PIXI.Sprite(P3.assets.texture("paper"));
        this._paper.anchor.set(0.5);
        this._paper.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this._paper.scale.set(2);
        this._drawingLayer.addChild(this._paper);

        this._drawing = new Drawing();
        // this._drawing.position.set(0, -300);
        this._drawing.signals.beginPath.add(this.onBeginPath, this);
        this._drawing.signals.endPath.add(this.onEndPath, this);
        this._drawing.init();
        this._drawing.enabled = false;
        this._drawingLayer.addChild(this._drawing);

        this._camera = new P3.NewCamera(
            new PIXI.Point(0, 0),
            new PIXI.Point(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5)
        );
        this._camera.zoom = 0.5;
        this._camera.addLayer(new P3.CameraLayer(this._drawingLayer, new PIXI.Point(1, 1)));

        this._interaction = new PIXI.Container();
        this._interaction.interactive = true;
        this._interaction.hitArea = new PIXI.Rectangle(0, 0, common.STAGE_WIDTH, common.STAGE_HEIGHT);
        this.addChild(this._interaction);

        this.addInteractionListeners();

        this._camera.setTarget(new PIXI.Point(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5));

        let states = new P3.ButtonStates();
        states.normal = P3.assets.texture('btn_primary_medium_out');
        states.over = P3.assets.texture('btn_primary_medium_over');
        states.down = P3.assets.texture('btn_primary_medium_down');
        states.icon = P3.assets.texture('icon_redraw');

        this._redoButton = new P3.Button(states);
        this._redoButton.signals.click.add(this.onRedoClick, this);
        this._redoButton.enabled = false;
        this._redoButton.visible = false;
        this._redoButton.alpha = 0.7;
        this._redoButton.position.set(common.STAGE_WIDTH * 0.5 + 220, common.STAGE_HEIGHT - 85);
        this._redoButton.cursor = 'hover';
        this.addChild(this._redoButton);

        states = new P3.ButtonStates();
        states.normal = P3.assets.texture('btn_primary_large_out');
        states.over = P3.assets.texture('btn_primary_large_over');
        states.down = P3.assets.texture('btn_primary_large_down');
        states.icon = P3.assets.texture('icon_done');

        this._approveButton = new P3.Button(states);
        this._approveButton.clickSoundName = "sfx_click";
        this._approveButton.overSoundName = "sfx_rollover";
        this._approveButton.signals.click.add(this.onApproveButtonClick, this);
        this._approveButton.position.set(common.STAGE_WIDTH * 0.5 + 395, common.STAGE_HEIGHT - 110);

        this._approveButton.enabled = false;
        this._approveButton.visible = false;
        this._approveButton.alpha = 0.7;
        this._approveButton.cursor = 'hover';

        this.addChild(this._approveButton);

        states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_tertiary_small_out");
        states.over = P3.assets.texture("btn_tertiary_small_over");
        states.down = P3.assets.texture("btn_tertiary_small_down");
        states.icon = P3.assets.texture("icon_sound_on");
        states.iconAlt = P3.assets.texture("icon_sound_off");

        this._muteButton = new P3.MuteButton(states);
        this._muteButton.clickSoundName = "sfx_click";
        this._muteButton.overSoundName = "sfx_rollover";
        this._muteButton.signals.up.add(function () {
            common.saveData.data.mute = !P3.audio.isMuted();
            common.saveData.save();
        }, this);
        this._muteButton.cursor = 'hover';
        this.addChild(this._muteButton);

        this._blueprintCarousel = new BlueprintCarousel(11);
        this._blueprintCarousel.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this._blueprintCarousel.signals.selected.once(this.onBlueprintSelected, this);
        this.addChild(this._blueprintCarousel);

        this.dialog = new ConfirmRestartDialog(translate('undo_confirm'));
        this.dialog.position.set(common.STAGE_WIDTH * 0.5 - 80, common.STAGE_HEIGHT * 0.5 - 80);
        this.dialog.visible = false;
        this.addChild(this.dialog);

        this._confirmDrawingDialog = new ConfirmDialog(translate("ready"));
        this._confirmDrawingDialog.position.set(common.STAGE_WIDTH * 0.5 + 200, common.STAGE_HEIGHT * 0.5);
        this._confirmDrawingDialog.visible = false;
        this.addChild(this._confirmDrawingDialog);

        this._confirmDrawingDialog.signals.confirm.once(this.onConfirmDrawing, this);
        this._confirmDrawingDialog.signals.deny.add(this.onRedoDrawing, this);

        this._confirmRestartDialog = new ConfirmRestartDialog(translate("restart_confirm"));
        this._confirmRestartDialog.position.set(common.STAGE_WIDTH * 0.5 + 200, common.STAGE_HEIGHT * 0.5);
        this._confirmRestartDialog.visible = false;
        this._confirmRestartDialog.bg.visible = false;
        this.addChild(this._confirmRestartDialog);

        this._confirmRestartDialog.signals.confirm.once(this.onConfirmRestart, this);
        this._confirmRestartDialog.signals.deny.add(this.onCancelRestart, this);

        this._colorPanel = new ColorPanel(common.config.colors);
        this._colorPanel.position.set(common.STAGE_WIDTH * 0.5 + 390, 0);
        this._colorPanel.visible = false;
        this._colorPanel.signals.colorSelected.add(this.onColor, this);
        this.addChild(this._colorPanel);

        this._loopSuffixes = ["a", "b", "c", "d"];
        this._suffixIndex = 0;

        this._drawing.enabled = false;
    }

    animateTom() {

    }

    destroy() {
        //  this._ambientLoop && this._ambientLoop.stop();
        super.destroy();
        this.removeInteractionListener();
    }

    resize() {
        super.resize();

        this._drawing.resize();

        this._muteButton.position.set((common.STAGE_WIDTH - P3.View.width) * 0.5 + 90, 90);

    }

    animateIn() {
        /*
        this._playButton.alpha = 0.3;
        new P3.utils.TWEEN.Tween(this._playButton, this._tweens)
            .delay(190)
            .onStart(() => this._playButton.visible = true)
            .to({alpha: 1}, 210)
            .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
            .start();
         */
    }

    animateOut() {
        P3.utils.delayg(this._tweens, 2.5, () => this.signals.next.dispatch());
    }

    update(dt = window.deltaTime) {
        super.update();

        this._blueprintCarousel.visible && this._blueprintCarousel.update(dt);

        this._camera.update();
        this._drawing.update();
        this._colorPanel.update();

        if (this._spine) {
            this._spine.update(dt);
            this._pencilSpine.update(dt);
        }

        this._progressBar && this._progressBar.update();

        this._confirmDrawingDialog.update();
        this._confirmRestartDialog.update();
        this.dialog.update();

        //  this._drawing.enabled = !this._drawLock;
    }

    end() {
        this._confirmDrawingDialog.visible = true;
        this._colorPanel.visible = false;

        this._progressBar.visible = false;
        this._redoButton.visible = false;
        this._approveButton.visible = false;

        // show all hidden slots
        for (let i = 0; i < this._spine.skeleton.slots.length; i++) {
            const slot = this._spine.skeleton.slots[i];
            const sprite = slot.currentSprite ? slot.currentSprite : slot.currentMesh;
            if (sprite)
                sprite.visible = true;
        }

        // animate briefly before scene transition
        this._spine.state.setAnimation(0, "chase", true);

        this._spine.filters = [this._glowFilter, this._alphaFilter];

        new P3.utils.TWEEN.Tween(this._camera.target, this._tweens)
            .to({
                x: this._camera.target.x + 500,
                y: common.STAGE_HEIGHT * 0.5
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .start();
    }

    addInteractionListeners() {
        this._interaction.on('mousedown', this._drawing.onPointerDown, this._drawing);
        this._interaction.on('mousemove', this._drawing.onPointerMove, this._drawing);
        this._interaction.on('mouseup', this._drawing.onPointerUp, this._drawing);
        this._interaction.on('mouseupoutside', this._drawing.onPointerUp, this._drawing);
        this._interaction.on('touchstart', this._drawing.onPointerDown, this._drawing);
        this._interaction.on('touchmove', this._drawing.onPointerMove, this._drawing);
        this._interaction.on('touchend', this._drawing.onPointerUp, this._drawing);
        this._interaction.on('touchendoutside', this._drawing.onPointerUp, this._drawing);
    }

    removeInteractionListener() {
        this._interaction.off('mousedown', this._drawing.onPointerDown, this._drawing);
        this._interaction.off('mousemove', this._drawing.onPointerMove, this._drawing);
        this._interaction.off('mouseup', this._drawing.onPointerUp, this._drawing);
        this._interaction.off('mouseupoutside', this._drawing.onPointerUp, this._drawing);
        this._interaction.off('touchstart', this._drawing.onPointerDown, this._drawing);
        this._interaction.off('touchmove', this._drawing.onPointerMove, this._drawing);
        this._interaction.off('touchend', this._drawing.onPointerUp, this._drawing);
        this._interaction.off('touchendoutside', this._drawing.onPointerUp, this._drawing);
    }

    nextSlot(repeat = false) {

        if (this._suffixIndex < this._loopSuffixes.length) {
            const suffix = this._loopSuffixes[this._suffixIndex];
            P3.audio.playMusic("music_drawing_loop_" + suffix + "_00");
            console.log("playing: ", suffix);
        }

        const t = repeat ? this._slots[this._slotIndex] : this._slots[++this._slotIndex];

        if (repeat) {
            this._suffixIndex--;
            if (this._suffixIndex < 0)
                this._suffixIndex = 0;
        } else {
            this._suffixIndex++;
            if (this._suffixIndex >= this._loopSuffixes.length)
                this._suffixIndex = this._loopSuffixes.length - 1;
        }

        this._colorPanel.animateOut();

        t.name = "" + (this._slotIndex + 1);
        t.order = this._slotIndex + 1;

        const slot = this._spine.skeleton.findSlot(t.name);

        if (!slot)
            throw new Error("missing slot : " + t.name)

        // set drawing style
        this._drawing.style = t.style;
        this._drawing.beginSlot(t.boolean, t.name);
        this._drawing.enabled = true;
        this._drawing.grouped = t.grouped;

        if (repeat) {
            const sprite = slot.currentSprite ? slot.currentSprite : slot.currentMesh;// first try sprite, fallback to mesh
            const a = new P3.utils.TWEEN.Tween(sprite, this._tweens)
                .to({alpha: 0}, 420)
                .easing(P3.utils.TWEEN.Easing.Quadratic.In)
                .onComplete(() => {
                    this._spine.hackTextureBySlotName(t.name, this._originalTexture, this._originalTexture.orig);
                    sprite.scale = this._originalScale;
                    sprite.anchor && sprite.anchor.set(0.5); // set anchor only on sprites (not mesh, doesnt have an anchor)
                });

            const b = new P3.utils.TWEEN.Tween(sprite, this._tweens)
                .to({alpha: 1}, 420)
                .easing(P3.utils.TWEEN.Easing.Quadratic.Out);

            a.chain(b);
            a.start();
        }

        this._fadetween && this._fadetween.stop();

        // hide the slot whilst we are drawing
        const sprite = slot.currentSprite ? slot.currentSprite : slot.currentMesh;// first try sprite, fallback to mesh
        sprite && (sprite.visible = true);
        //sprite && (sprite.alpha = this._isDrawing ? 0.5 : 1); // whats this line for?

        // display the guide
        this._pencilSpine.state.setAnimation(0, t.name, false);
        this._pencilSpine.alpha = 0;
        new P3.utils.TWEEN.Tween(this._pencilSpine, this._tweens)
            .to({alpha: 1}, 190)
            .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
            .start();

        const localPos = this._drawingLayer.toLocal(new PIXI.Point(sprite.x, sprite.y), sprite.parent);

        // animate camera
        new P3.utils.TWEEN.Tween(this._camera.target, this.tweens)
            .to({
                x: localPos.x,
                y: localPos.y
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .onStart(() => this.onZoomBegin())
            .onComplete(() => this.onZoomComplete())
            .start();

        new P3.utils.TWEEN.Tween(this._camera, this.tweens)
            .to({
                zoom: t.camera.zoom
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Cubic.InOut)
            .start();

        this._spine.update(0);

    }

    createSpine(id) {
        this._spine = new DrawingSpine(P3.assets.spineData("ghost_" + P3.utils.padNum(id, 2)));
        this._spine.autoUpdate = false;
        this._spine.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this._drawingLayer.addChild(this._spine);

        this._pencilSpine = new PIXI.spine.Spine(P3.assets.spineData("ghost_" + P3.utils.padNum(id, 2) + "_path"));
        this._pencilSpine.autoUpdate = false;
        this._pencilSpine.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
        this._drawingLayer.addChild(this._pencilSpine);

        this._pencil = this._pencilSpine.skeleton.findSlot("guide_pencil");

        // hide sprites
        const slots = this._spine.skeleton.slots;
        for (let i = 0; i < slots.length; i++) {
            slots[i].currentSprite && (slots[i].currentSprite.visible = false);
        }

        this._spine.skeleton.setSlotsToSetupPose();
    }

    generateHack() {

        this._groupcount = 0;
        this._drawing.grouped = false;
        this._drawing.enabled = false;

        const path = this._path;
        const source = this._source;

        if (path) {
            this._isDrawing = false;
            const t = this._slots[this._slotIndex];
            const slot = this._spine.skeleton.findSlot("" + t.name);

            const sprite = slot.currentSprite ? slot.currentSprite : slot.currentMesh;// first try sprite, fallback to mesh

            const center = this._drawingLayer.toLocal(sprite.position, sprite.parent);

            path.name = "path_" + t.name;

            this._originalTexture = sprite.texture.clone();
            this._originalScale = sprite.scale.clone();

            const hack = DrawingTextureHack.create(path, slot, center);

            this._spine.addTextureHack(hack);

            sprite && (sprite.visible = true);

            source.enabled = false;
        }

    }

    toggleApproveButton(activate) {

        if (activate) {
            this._approveButton.alpha = this._redoButton.alpha = 1;
            this._approveButton.enabled = this._redoButton.enabled = true;
            // this._approveButton.filters = this._redoButton.filters = null;
        } else {
            this._approveButton.enabled = this._redoButton.enabled = false;
            this._approveButton.alpha = this._redoButton.alpha = 0.7;
        }

    }

    randomCelebration() {

    }

    onBeginPath() {

        if (this._drawLock)
            return;

        this._isDrawing = true;

        this._drawingsound && this._drawingsound.stop();
        this._drawingsound = P3.audio.playSound('sfx_drawing_loop_00', {loop: true});

        new P3.utils.TWEEN.Tween(this._pencilSpine, this.tweens)
            .to({
                alpha: 0
            }, 150)
            .easing(P3.utils.TWEEN.Easing.Cubic.InOut)
            .start();

    }

    onEndPath(source, path) {

        this._drawingsound && this._drawingsound.stop();
        if (!this._drawLock) P3.audio.playSound('sfx_drawing_fill_00');

        if (this._drawLock || (this._slots[this._slotIndex].grouped && ++this._groupcount < this._slots[this._slotIndex].groupcount))
            return;

        this._source = source;
        this._path = path;

        this._drawLock = true;
        this._drawing.enabled = false;

        // animate camera zoom out
        new P3.utils.TWEEN.Tween(this._camera.target, this.tweens)
            .to({
                x: common.STAGE_WIDTH * 0.5,
                y: common.STAGE_HEIGHT * 0.5
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .onComplete(() => {

                this.toggleApproveButton(true);
                this._slots[this._slotIndex].style.fill.enabled && this._colorPanel.animateIn();

            })
            .start();

        new P3.utils.TWEEN.Tween(this._camera, this.tweens)
            .to({
                zoom: 0.5
            }, 700)
            .easing(P3.utils.TWEEN.Easing.Cubic.InOut)
            .start();

        this.generateHack();
    }

    onColor(button) {

        if (this._path) {

            if (button.color) {

                const strokeColor = new P3.math.Color().setHex(button.color);

                if (this._drawing.style.fill.enabled) {
                    this._path.fillColor = button.color;
                    strokeColor.v = 0.5;
                }

            } else {
                // if no color its the clear color button
                if (this._drawing.style.fill.enabled)
                    this._path.fillColor = this._drawing.style.fill.color;

                this._path.strokeColor = this._drawing.style.stroke.color;

                if (this._path.fillColor && this._path.fillColor.gradient) {
                    this._path.fillColor.gradient.origin = this._path.bounds.leftCenter;
                    this._path.fillColor.gradient.destination = this._path.bounds.rightCenter;

                    this._path.fillColor.origin = this._path.bounds.leftCenter;
                    this._path.fillColor.destination = this._path.bounds.rightCenter;
                }
            }

            this.generateHack();

        }

        P3.audio.playSound("sfx_change_colour_fill_00");

    }

    onApproveButtonClick() {
        if (this._slotIndex === this._slots.length - 1) {
            this.end();
        } else {
            this._groupcount = 0;
            this.nextSlot(false);
            this._progressBar.updateFill(1);
        }

        this.toggleApproveButton(false);
    }

    onBlueprintSelected() {
        this._blueprintCarousel.animateOut(() => {
            const id = this._blueprintCarousel.index + 1;
            this.createSpine(id);
            this._slots = P3.assets.json("ghost_" + P3.utils.padNum(id, 2) + "_data");

            this._pencilSpine.visible = false;

            common.ghostId = id;

            this._progressBar = new ProgressBar(this._slots.length);
            this._progressBar.position.set(common.STAGE_WIDTH * 0.5 - 180, common.STAGE_HEIGHT - 140);
            this.addChild(this._progressBar);

            this._spine.alpha = 0;

            this._drawing.enabled = false;

            this._drawLock = true;

            this._progressBar.alpha = 0;
            new P3.utils.TWEEN.Tween(this._progressBar, this._tweens)
                .to({alpha: 1}, 320)
                .easing(P3.utils.TWEEN.Easing.Quadratic.In)
                .onComplete(() => {
                    this._redoButton.scale.set(0);
                    this._redoButton.visible = true;
                    new P3.utils.TWEEN.Tween(this._redoButton.scale, this._tweens)
                        .to({x: 1, y: 1}, 320)
                        .easing(P3.utils.TWEEN.Easing.Back.Out)
                        .onComplete(() => {
                            this._approveButton.scale.set(0);
                            this._approveButton.visible = true;
                            new P3.utils.TWEEN.Tween(this._approveButton.scale, this._tweens)
                                .to({x: 1, y: 1}, 320)
                                .easing(P3.utils.TWEEN.Easing.Back.Out)
                                .onComplete(() => {


                                    new P3.utils.TWEEN.Tween(this._spine, this._tweens)
                                        .to({alpha: 1}, 90)
                                        .easing(P3.utils.TWEEN.Easing.Quadratic.In)
                                        .onComplete(() => {

                                            this._progressBar.updateFill(1);
                                            this._drawLock = false;

                                            this._pencilSpine.visible = true;
                                            this.nextSlot(false);
                                            this._drawing.enabled = true;


                                        })
                                        .start();
                                })
                                .start();
                        })
                        .start();
                })
                .start();


        });
    }

    onRedoClick() {
        if (this.dialog.visible) return;
        this.dialog.visible = true;

        this.dialog.signals.confirm.once(() => {
            P3.audio.playSound('sfx_erase_item_00');
            this._groupcount = 0;
            this.nextSlot(true);

            this._approveButton.enabled = this._redoButton.enabled = false;
            this._approveButton.alpha = this._redoButton.alpha = 0.7;
            this.dialog.visible = false;

        }, this);
        this.dialog.signals.deny.once(() => {
            this.dialog.visible = false;
        });
    }

    onBackClick() {
        this.signals.home.dispatch();
    }

    onZoomBegin() {
        this._drawLock = true;
        this._drawing.enabled = false;
    }

    onZoomComplete() {
        this._drawLock = false;
        this._drawing.enabled = true;
    }

    onUnzoomBegin() {

    }

    onUnzoomComplete() {

    }

    onConfirmRestart() {
        this.signals.previous.dispatch();
    }

    onCancelRestart() {
        this._confirmDrawingDialog.visible = true;
        this._confirmRestartDialog.visible = false;
    }

    onConfirmDrawing() {
        P3.audio.stopMusic();
        P3.audio.playSound("music_drawing_loop_e_end_00");

        this._confirmDrawingDialog.visible = false;

        common.saveData.saveSpineHacks("ghost", this._spine);

        this.animateOut();
    }

    onRedoDrawing() {
        this._confirmDrawingDialog.visible = false;
        this._confirmRestartDialog.visible = true;
    }
}